import React, { FormEventHandler, useCallback, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';

import { AVAILABLE_REGIONS, ensureAgentHubBrands } from '~/consts/agentHub';

import { useCreateEOI } from '~/hooks/apis/agent-hub/useCreateEOI';
import { useListAgencies } from '~/hooks/apis/agent-hub/useListAgencies';
import { useGetAffiliationList } from '~/hooks/apis/agent/useGetAffiliationList';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { CreateEoiParams } from '~/types/services/agentHub';

type EoiCreateRegionCode = NonNullable<CreateEoiParams['regionCode']>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FORM_FIELD_NAMES = {
  agencyId: 'agencyId',
  givenName: 'givenName',
  surname: 'surname',
  email: 'email',
  phone: 'phone',
  regionCode: 'regionCode',
  affiliationId: 'affiliationId',
} as const;

export default function CreateUserSubmissionModal({ isOpen, onClose, onSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useCurrentTenant();
  const formRef = useRef<HTMLFormElement>(null);
  const createEOIMutation = useCreateEOI();

  const [error, setError] = useState<string | null>(null);
  const [regionCode, setRegionCode] = useState<EoiCreateRegionCode>('AU');
  const [affiliationId, setAffiliationId] = useState('');
  const [agencyId, setAgencyId] = useState('');

  const brand = useMemo(() => {
    try {
      return ensureAgentHubBrands(tenant.tenant.brand);
    } catch {
      return 'leagenthub';
    }
  }, [tenant.tenant.brand]);

  const { data: affiliations = [], isLoading: isLoadingAffiliations } = useGetAffiliationList(regionCode);

  const { data: agencies = [], isLoading: isLoadingAgencies } = useListAgencies({
    brand,
    affiliationId,
  });

  const handleRegionChange = useCallback((e: SelectChangeEvent<string>) => {
    setRegionCode(e.target.value as EoiCreateRegionCode);
    setAffiliationId('');
    setAgencyId('');
  }, []);

  const handleAffiliationChange = useCallback((e: SelectChangeEvent<string>) => {
    setAffiliationId(e.target.value);
    setAgencyId('');
  }, []);

  const handleAgencyChange = useCallback((e: SelectChangeEvent<string>) => {
    setAgencyId(e.target.value);
  }, []);

  const resetForm = useCallback(() => {
    formRef.current?.reset();
    setError(null);
    setRegionCode('AU');
    setAffiliationId('');
    setAgencyId('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (event) => {
      event.preventDefault();
      setError(null);

      const formData = new FormData(event.currentTarget);
      const givenName = String(formData.get(FORM_FIELD_NAMES.givenName) || '').trim();
      const surname = String(formData.get(FORM_FIELD_NAMES.surname) || '').trim();
      const email = String(formData.get(FORM_FIELD_NAMES.email) || '').trim();
      const phone = String(formData.get(FORM_FIELD_NAMES.phone) || '').trim();

      if (!affiliationId) {
        setError('Please select an affiliation');
        return;
      }

      if (!agencyId) {
        setError('Please select an agency');
        return;
      }

      if (!givenName || !surname || !email) {
        setError('Please fill in all required fields');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address');
        return;
      }

      createEOIMutation.mutate(
        {
          affiliationId,
          agencyId,
          brand,
          email,
          givenName,
          phone,
          regionCode,
          surname,
        },
        {
          onSuccess: () => {
            enqueueSnackbar('User submission created successfully', { variant: 'success' });
            resetForm();
            onClose();
            onSuccess();
          },
          onError: (e) => {
            const errorMessage = e instanceof Error ? e.message : 'Failed to create user submission';
            setError(errorMessage);
            enqueueSnackbar(errorMessage, { variant: 'error' });
          },
        },
      );
    },
    [affiliationId, agencyId, brand, regionCode, resetForm, onClose, onSuccess, enqueueSnackbar, createEOIMutation],
  );

  const renderAffiliationMenuItems = () => {
    if (isLoadingAffiliations) {
      return <MenuItem disabled>Loading affiliations...</MenuItem>;
    }

    if (affiliations.length === 0) {
      return <MenuItem disabled>No affiliations available</MenuItem>;
    }

    return affiliations.map((affiliation) => (
      <MenuItem key={affiliation.id} value={affiliation.id}>
        {affiliation.name}
      </MenuItem>
    ));
  };

  const renderAgencyMenuItems = () => {
    if (isLoadingAgencies) {
      return <MenuItem disabled>Loading agencies...</MenuItem>;
    }

    if (agencies.length === 0) {
      return <MenuItem disabled>No agencies available</MenuItem>;
    }

    return agencies.map((agency) => (
      <MenuItem key={agency.id} value={agency.id}>
        {agency.name} ({agency.businessCodeType}: {agency.businessCodeValue})
      </MenuItem>
    ));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      disableEscapeKeyDown={createEOIMutation.isPending}
      maxWidth="sm"
      fullWidth
    >
      <form ref={formRef} onSubmit={handleSubmit}>
        <DialogTitle>Create New User Submission</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <FormControl fullWidth required>
              <InputLabel>Region</InputLabel>
              <Select
                name={FORM_FIELD_NAMES.regionCode}
                value={regionCode}
                label="Region"
                onChange={handleRegionChange}
              >
                {AVAILABLE_REGIONS.map((region) => (
                  <MenuItem key={region.value} value={region.value}>
                    {region.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Affiliation</InputLabel>
              <Select
                name={FORM_FIELD_NAMES.affiliationId}
                value={affiliationId}
                label="Affiliation"
                onChange={handleAffiliationChange}
                disabled={isLoadingAffiliations || affiliations.length === 0}
              >
                {renderAffiliationMenuItems()}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Agency</InputLabel>
              <Select
                name={FORM_FIELD_NAMES.agencyId}
                value={agencyId}
                label="Agency"
                onChange={handleAgencyChange}
                disabled={isLoadingAgencies || agencies.length === 0 || !affiliationId}
              >
                {renderAgencyMenuItems()}
              </Select>
            </FormControl>

            <TextField label="First Name" name={FORM_FIELD_NAMES.givenName} required fullWidth />

            <TextField label="Last Name" name={FORM_FIELD_NAMES.surname} required fullWidth />

            <TextField label="Email" name={FORM_FIELD_NAMES.email} type="email" required fullWidth />

            <TextField label="Phone" name={FORM_FIELD_NAMES.phone} placeholder="e.g., 61 402123456" fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="text" disabled={createEOIMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={createEOIMutation.isPending || isLoadingAffiliations}>
            {createEOIMutation.isPending ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
